import { useEffect, useRef } from 'react'
import QRCodeLib from 'qrcode'
import { logError } from '../lib/log'

interface Props {
  value: string
  /** Pixel size of the rendered QR. Default 240. */
  size?: number
  /** Override error-correction level. Default 'M'. Use 'H' for denser payloads. */
  errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H'
  className?: string
  ariaLabel?: string
}

/**
 * Renders an arbitrary string to a QR canvas. Failures are surfaced via the
 * shared logger (never silently swallowed) and the canvas is wiped so a
 * stale QR from a previous render can't be mistaken for a current one.
 */
export function QrCanvas({
  value,
  size = 240,
  errorCorrectionLevel = 'M',
  className,
  ariaLabel,
}: Props) {
  const ref = useRef<HTMLCanvasElement | null>(null)

  useEffect(() => {
    const canvas = ref.current
    if (!canvas) return
    QRCodeLib.toCanvas(canvas, value, {
      errorCorrectionLevel,
      width: size,
      margin: 1,
    }).catch((err: unknown) => {
      const ctx = canvas.getContext('2d')
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height)
      logError('qr.render failed', err, { valueLength: value.length })
    })
  }, [value, size, errorCorrectionLevel])

  return (
    <canvas
      ref={ref}
      className={className}
      role="img"
      aria-label={ariaLabel ?? 'QR code'}
      width={size}
      height={size}
    />
  )
}
